const MAX_STRINGS = 500;

/**
 * @param {Function} axios
 * @param {string[]} texts
 * @param {{ source: string, target?: string, paths?: string[], additionalPrompt?: string, uiInfoConfig?: string, localeRules?: string, restrictedTerms?: string[], model?: string, mode?: string, bucket?: string }} locales
 */
export async function translateStrings(axios, texts, locales) {
  if (!texts.length) return [];

  if (texts.length > MAX_STRINGS) {
    const err = new Error(`Too many strings to translate (${texts.length}). Maximum is ${MAX_STRINGS}.`);
    err.status = 400;
    throw err;
  }

  const options =
    typeof locales === 'string' ? { source: locales, target: 'en' } : { ...locales };

  const {
    source,
    target = 'en',
    paths,
    additionalPrompt,
    uiInfoConfig,
    localeRules,
    restrictedTerms,
    model,
    mode
  } = options;

  const baseURL = process.env.VUE_APP_BACKEND_URL || '';
  const timeoutMs = Number(process.env.VUE_APP_TRANSLATE_TIMEOUT_MS) || 600000;

  const body = {
    texts,
    source,
    target,
    bucket: options.bucket
  };

  if (mode === 'compare') {
    body.mode = 'compare';
  } else {
    body.paths = paths;
    body.additionalPrompt = additionalPrompt || '';
    body.uiInfoConfig = uiInfoConfig || '';
    body.localeRules = localeRules || '';
    body.restrictedTerms = Array.isArray(restrictedTerms) ? restrictedTerms : [];
    body.model = model || undefined;
  }

  const response = await axios.post(`${baseURL}/storage/translate`, body, {
    timeout: timeoutMs
  });

  return response.data.translations || [];
}

/**
 * Apply reviewer feedback to existing locale strings (revises in place).
 * @param {Function} axios
 * @param {string[]} texts
 * @param {{ locale: string, paths?: string[], feedback: string, uiInfoConfig?: string, localeRules?: string, restrictedTerms?: string[], model?: string }} options
 */
export async function refineStrings(axios, texts, options) {
  if (!texts.length) return [];

  if (texts.length > MAX_STRINGS) {
    const err = new Error(`Too many strings to refine (${texts.length}). Maximum is ${MAX_STRINGS}.`);
    err.status = 400;
    throw err;
  }

  const { locale, paths, feedback, uiInfoConfig, localeRules, restrictedTerms, model } = options;
  const feedbackText = String(feedback || '').trim();
  if (!feedbackText) {
    const err = new Error('Feedback is required');
    err.status = 400;
    throw err;
  }

  const baseURL = process.env.VUE_APP_BACKEND_URL || '';
  const timeoutMs = Number(process.env.VUE_APP_TRANSLATE_TIMEOUT_MS) || 600000;

  const response = await axios.post(
    `${baseURL}/storage/translate`,
    {
      texts,
      mode: 'refine',
      locale,
      source: locale,
      bucket: options.bucket,
      paths,
      feedback: feedbackText,
      uiInfoConfig: uiInfoConfig || '',
      localeRules: localeRules || '',
      restrictedTerms: Array.isArray(restrictedTerms) ? restrictedTerms : [],
      model: model || undefined
    },
    { timeout: timeoutMs }
  );

  return response.data.translations || [];
}
